import React, { useState, useEffect } from 'react';
import StorylineOverlay from './StorylineOverlay';
import MusicalCountsOverlay from './MusicalCountsOverlay';
import TechnicalNotesOverlay from './TechnicalNotesOverlay';
import ReferenceOverlay from './ReferenceOverlay';
import storylineData from '../data/storylineNotes.json';
import musicalCountsData from '../data/musicalCounts.json';
import technicalNotesData from '../data/technicalNotes.json';
import referenceData from '../data/referenceNotes.json';
import { formatTime } from '../utils/timeUtils';

const NotesPanel = ({ 
  currentTime, 
  showStoryline, 
  showMusicalCounts, 
  showTechnicalNotes,
  showReference,
  storylineOnVideo,
  musicalCountsOnVideo,
  technicalNotesOnVideo,
  videoControls,
  textSize = 100
}) => {
  const [hasStorylineNotes, setHasStorylineNotes] = useState(false);
  const [hasMusicalCounts, setHasMusicalCounts] = useState(false);
  const [hasTechnicalNotes, setHasTechnicalNotes] = useState(false);
  const [hasReferenceNotes, setHasReferenceNotes] = useState(false);
  
  // Check if there are notes at the current timestamp
  useEffect(() => {
    // Buffer time (in seconds) to keep a note visible after its timestamp
    const bufferTime = 5;
    
    // Check for storyline notes
    const storylineNote = storylineData.find(note => {
      return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
    });
    setHasStorylineNotes(!!storylineNote);
    
    // Check for musical counts
    let closestCount = null;
    let smallestDifference = Infinity;
    
    for (const count of musicalCountsData) {
      if (count.seconds <= currentTime) {
        const difference = currentTime - count.seconds;
        if (difference < smallestDifference) {
          smallestDifference = difference;
          closestCount = count;
        }
      }
    }
    
    setHasMusicalCounts(!!closestCount && smallestDifference <= 1);
    
    // Check for technical notes
    const technicalNote = technicalNotesData.find(note => {
      return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
    });
    setHasTechnicalNotes(!!technicalNote);
    
    // Check for reference notes
    const referenceNote = referenceData.find(note => {
      return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
    });
    setHasReferenceNotes(!!referenceNote);
  }, [currentTime]);
  
  // We don't apply text size to the side panel anymore
  
  return (
    <div className="notes-panel bg-gray-900 rounded-lg p-4 h-full flex flex-col">
      <div className="mb-4">
        <div className="flex justify-end items-center mb-2">
          <div className="text-white text-sm font-mono bg-gray-800 px-2 py-1 rounded">
            {formatTime(currentTime)}
          </div>
        </div>
      </div>
      
      <div className="notes-content flex-1 overflow-y-auto">
        {/* Show storyline section if it's enabled and not on video */}
        {showStoryline && !storylineOnVideo && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Storyline</h3>
            <div className="bg-gray-800 rounded p-3">
              <StorylineOverlay currentTime={currentTime} inPanel={true} videoControls={videoControls} />
              {!hasStorylineNotes && (
                <p className="text-gray-400 italic">No storyline notes at this timestamp.</p>
              )}
            </div>
          </div>
        )}
        
        {/* Show musical counts section if it's enabled and not on video */}
        {showMusicalCounts && !musicalCountsOnVideo && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Musical Counts</h3>
            <div className="bg-gray-800 rounded p-3">
              <MusicalCountsOverlay currentTime={currentTime} inPanel={true} />
              {!hasMusicalCounts && (
                <p className="text-gray-400 italic">No musical counts at this timestamp.</p>
              )}
            </div>
          </div>
        )}
        
        {/* Show technical notes section if it's enabled and not on video */}
        {showTechnicalNotes && !technicalNotesOnVideo && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Technical Notes</h3>
            <div className="bg-gray-800 rounded p-3">
              <TechnicalNotesOverlay currentTime={currentTime} inPanel={true} />
              {!hasTechnicalNotes && (
                <p className="text-gray-400 italic">No technical notes at this timestamp.</p>
              )}
            </div>
          </div>
        )}
        
        {/* Show reference section if it's enabled */}
        {showReference && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Reference</h3>
            <div className="bg-gray-800 rounded p-3">
              <ReferenceOverlay currentTime={currentTime} inPanel={true} videoControls={videoControls} />
              {!hasReferenceNotes && (
                <p className="text-gray-400 italic">No references at this timestamp.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPanel;
